// client/src/EventDetail.js
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import ChatBox from './ChatBox';
import ShareCard from './ShareCard';
import ReportEvent from './ReportEvent';
import { HOME_THEME_COLOR, primaryColor } from './eventBackground';
import { toolbarButton, toolbarButtonActive } from './toolbarButton';
import { useAuth } from './AuthProvider';
import { API_BASE as API_ORIGIN } from './api';
import { formatDateTimeRange } from './formatDate';
import FormattedText from './FormattedText';
import { navState } from './navState';

const API_BASE = `${API_ORIGIN}/api`;

const HEADER_OVERLAY = 30;
const EXTRA_OVERLAY = 15;
const HERO_HEIGHT = 'min(42vh, 520px)';

function userKeyFromUser(user) {
  return user?.id || user?.email || user?.username || null;
}

function getRsvpSet(key) {
  try {
    const raw = localStorage.getItem(`rsvps:${key || 'anon'}`) || '[]';
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveRsvpSet(key, set) {
  localStorage.setItem(
    `rsvps:${key || 'anon'}`,
    JSON.stringify(Array.from(set))
  );
}


export default function EventDetail({ events = [], deleteEvent }) {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  // Opened with the smooth page transition (App.js), which already slides the
  // page in; only animate here when it wasn't.
  // Also not on the page the site was loaded on: the whole page slides up then.
  // Worked out once per event, so a later state change can't restart it.
  const smoothOpen = Boolean(location.state && location.state.smoothOpen);
  const arriveClass = useMemo(
    () => (smoothOpen || navState.firstPage ? '' : ' event-arrive'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  // Open the share card straight away for an event that was just created.
  const [showShare, setShowShare] = useState(Boolean(location.state?.justCreated));
  const { user } = useAuth();

  // Show the event straight away if the feed already has it (also lets the
  // feed card's picture animate into the banner), then refresh from the server.
  const findLocalEvent = (list) =>
    list.find((event) => event.slug === id || event.id === id || event._id === id) || null;
  const [ev, setEv] = useState(() => findLocalEvent(events));
  const [loading, setLoading] = useState(() => !findLocalEvent(events));
  const shownEventRef = useRef(ev);
  shownEventRef.current = ev;

  const userKey = userKeyFromUser(user);

  const [isRsvped, setIsRsvped] = useState(() => {
    const set = getRsvpSet(userKey);
    return set.has(id);
  });

  useEffect(() => {
    const set = getRsvpSet(userKey);
    setIsRsvped(set.has(id));
  }, [userKey, id]);

  useEffect(() => {
    let cancelled = false;

    async function loadEvent() {
      // Keep showing the event if it's already on screen; just refresh it.
      const shown = shownEventRef.current;
      if (!shown || ![shown.slug, shown.id, shown._id].includes(id)) {
        setLoading(true);
      }

      try {
        const res = await fetch(
          `${API_BASE}/events/${encodeURIComponent(id)}`,
          { headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {} }
        );

        if (res.ok) {
          const data = await res.json();

          if (!cancelled) {
            setEv(data);
            setLoading(false);
          }

          return;
        }
      } catch (err) {
        console.warn(
          'Backend event fetch failed, using fallback:',
          err
        );
      }

      const fallbackFromProps = events.find(
        (event) =>
          event.slug === id ||
          event.id === id ||
          event._id === id
      );

      if (fallbackFromProps) {
        if (!cancelled) {
          setEv(fallbackFromProps);
          setLoading(false);
        }

        return;
      }

      try {
        const saved = JSON.parse(
          localStorage.getItem('events') || '[]'
        );

        const fallbackFromStorage = saved.find(
          (event) =>
            event.slug === id ||
            event.id === id ||
            event._id === id
        );

        if (!cancelled) {
          setEv(fallbackFromStorage || null);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setEv(null);
          setLoading(false);
        }
      }
    }

    loadEvent();

    return () => {
      cancelled = true;
    };
  }, [id, events, user?.token]);

  const setRsvpCount = (count) => {
    setEv((current) => (current ? { ...current, rsvpCount: Math.max(0, count) } : current));
  };

  // RSVPs are remembered on this device (so the button stays pressed) and
  // counted on the server (so everyone sees the same total).
  const toggleRsvp = async () => {
    const set = getRsvpSet(userKey);
    const wasRsvped = set.has(id);
    const previousCount = Number(ev?.rsvpCount || 0);

    if (wasRsvped) {
      set.delete(id);
    } else {
      set.add(id);
    }
    saveRsvpSet(userKey, set);
    setIsRsvped(!wasRsvped);
    setRsvpCount(previousCount + (wasRsvped ? -1 : 1));

    try {
      const res = await fetch(
        `${API_BASE}/events/${encodeURIComponent(ev?.slug || id)}/rsvp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
          },
          body: JSON.stringify({ action: wasRsvped ? 'remove' : 'add' }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'RSVP failed');
      setRsvpCount(Number(data.rsvpCount || 0));
    } catch (rsvpError) {
      // Undo on failure so the button and count stay truthful.
      const undo = getRsvpSet(userKey);
      if (wasRsvped) {
        undo.add(id);
      } else {
        undo.delete(id);
      }
      saveRsvpSet(userKey, undo);
      setIsRsvped(wasRsvped);
      setRsvpCount(previousCount);
      console.warn('Could not update RSVP:', rsvpError);
    }
  };

  const signedName =
    user?.username ||
    user?.name ||
    (user?.email ? user.email.split('@')[0] : '');

  const [chatName, setChatName] = useState(() => {
    if (signedName) {
      return signedName;
    }

    return localStorage.getItem('chatName') || '';
  });

  useEffect(() => {
    if (signedName && chatName !== signedName) {
      setChatName(signedName);
    }
  }, [signedName]); // eslint-disable-line react-hooks/exhaustive-deps

  const pageBg =
    ev && ev.backgroundColor
      ? ev.backgroundColor
      : '#FF7A00';

  const isEventCreator = Boolean(
    ev && user && (
      (ev.creatorId && String(ev.creatorId) === String(user.id)) ||
      (ev.creatorEmail && String(ev.creatorEmail).toLowerCase() === String(user.email || '').toLowerCase())
    )
  );

  // Before the first paint, so the page never shows the site colour first.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--page-bg', pageBg);
    // Solid fallback behind the status bar, matching this event's colours.
    root.style.setProperty('--page-bg-solid', primaryColor(pageBg));

    const meta = document.querySelector(
      'meta[name="theme-color"]'
    );

    if (meta) {
      // theme-color only accepts a plain colour, not a gradient.
      meta.setAttribute('content', primaryColor(pageBg));
    }

    return () => {
      // Fall back to the site background defined in index.css.
      root.style.removeProperty('--page-bg');
      root.style.removeProperty('--page-bg-solid');

      if (meta) {
        meta.setAttribute('content', HOME_THEME_COLOR);
      }
    };
  }, [pageBg]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Loading event...</h2>
      </div>
    );
  }

  if (!ev) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Event not found 😢</h2>

        <p>
          <Link to="/">Go back home</Link>
        </p>
      </div>
    );
  }

  const handleDelete = async () => {
    const slug = ev?.slug || id;

    const confirmed = window.confirm(
      `Delete "${ev?.title || 'this event'}"? This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/events/${encodeURIComponent(slug)}`,
        {
          method: 'DELETE',
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data.error || 'Failed to delete event'
        );
      }

      if (typeof deleteEvent === 'function') {
        deleteEvent(slug);
      }

      try {
        const savedEvents = JSON.parse(
          localStorage.getItem('events') || '[]'
        );

        const updatedEvents = savedEvents.filter(
          (event) =>
            event.slug !== slug &&
            event.id !== slug &&
            event._id !== slug
        );

        localStorage.setItem(
          'events',
          JSON.stringify(updatedEvents)
        );
      } catch (storageError) {
        console.warn(
          'Could not remove event from localStorage:',
          storageError
        );
      }

      try {
        const rsvpSet = getRsvpSet(userKey);
        rsvpSet.delete(slug);
        rsvpSet.delete(id);
        saveRsvpSet(userKey, rsvpSet);
      } catch (rsvpError) {
        console.warn(
          'Could not remove RSVP:',
          rsvpError
        );
      }

      history.push('/');
    } catch (err) {
      console.error(
        'Event deletion failed:',
        err
      );

      alert(
        err.message || 'Failed to delete event'
      );
    }
  };

  const heroWrap = {
    width: '100vw',
    height: HERO_HEIGHT,
    marginLeft: 'calc(50% - 50vw)',
    marginRight: 'calc(50% - 50vw)',
    marginTop: `-${
      HEADER_OVERLAY + EXTRA_OVERLAY
    }px`,
    position: 'relative',
    overflow: 'hidden',
    zIndex: 0,
  };

  const heroImg = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  };

  const pageContainer = {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '1.5rem 1rem 2rem',
    fontFamily:
      'Helvetica, Arial, sans-serif',
  };

  const card = {
    background: '#fff',
    borderRadius: '0.5rem',
    boxShadow:
      '0 0 20px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '500px',
  };

  const content = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
    padding: '1rem',
    boxSizing: 'border-box',
  };


  // Same look as the footer's Admin / Sign out buttons.
  const btn = {
    ...toolbarButton,
    marginLeft: '0.5rem',
  };

  const btnActive = toolbarButtonActive;

  const infoRow = {
    margin: '0.5rem 0',
    fontSize: '0.95rem',
  };

  const footerControls = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
  };

  const chatPanel = {
    background: '#fff',
    borderRadius: '0.5rem',
    boxShadow:
      '0 0 20px rgba(0,0,0,0.1)',
    padding: '1rem',
    minWidth: 0,
    // Grows with the discussion instead of scrolling inside a fixed box.
    minHeight: 520,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'start',
  };

  return (
    <>
      {/* Slides up into view when opened from a card (index.css). */}
      <div className={arriveClass.trim() || undefined} style={heroWrap}>
        {ev.banner ? (
          <img
            src={ev.banner}
            alt="Event banner"
            style={heroImg}
          />
        ) : (
          // No picture: show the frog, sized to fit, over the event background.
          <div
            style={{
              ...heroImg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.25)',
            }}
          >
            <img
              src="/frolik-logo.png"
              alt=""
              style={{ height: '55%', width: 'auto', opacity: 0.85 }}
            />
          </div>
        )}
      </div>

      <div className={`event-page${arriveClass}${arriveClass ? ' is-second' : ''}`} style={pageContainer}>
        <div className="grid-event-layout">
          <div style={card}>
            <div style={content}>
              <div>
                {isEventCreator && ev.status === 'pending' && (
                  <div className="creator-review-status">Waiting for review</div>
                )}
                {isEventCreator && ev.status === 'rejected' && (
                  <div className="creator-review-status">
                    Not approved: {ev.rejectReason || 'Please review the event details.'}
                  </div>
                )}
                <div className="event-detail-header">
                  <div className="event-detail-heading">
                    <h2 className="event-detail-title" style={{ margin: 0 }}>
                      {ev.title}
                    </h2>

                    <div className="event-detail-info" style={infoRow}>
                      {formatDateTimeRange(ev.dateTime, ev.endDateTime)} ✶ {ev.location}
                    </div>
                  </div>

                  <div className="event-detail-actions">
                    {/* Shows "RSVP" until tapped, then how many are going. */}
                    <button
                      type="button"
                      onClick={toggleRsvp}
                      aria-pressed={isRsvped}
                      aria-live="polite"
                      title={isRsvped ? 'Tap to cancel your RSVP' : undefined}
                      style={
                        isRsvped
                          ? {
                              ...btn,
                              ...btnActive,
                            }
                          : btn
                      }
                    >
                      {isRsvped
                        ? `${Number(ev.rsvpCount || 0)} going`
                        : 'RSVP'}
                    </button>

                    <button
                      type="button"
                      style={
                        showShare
                          ? {
                              ...btn,
                              ...btnActive,
                            }
                          : btn
                      }
                      onClick={() => setShowShare((open) => !open)}
                      aria-expanded={showShare}
                    >
                      Share
                    </button>
                  </div>
                </div>

                <div
                  className="event-detail-description"
                  style={{
                    marginTop: '2.25rem',
                  }}
                >
                  {/* Shown exactly as written, with its formatting. */}
                  <FormattedText text={ev.description} />
                </div>

                {showShare && (
                  <ShareCard
                    event={ev}
                    url={`${window.location.origin}/event/${ev.slug || id}`}
                  />
                )}
              </div>

              <div className="event-detail-footer" style={footerControls}>
                <ReportEvent slug={ev.slug || id} token={user?.token} />

                {(isEventCreator || user?.role === 'admin') && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    style={btn}
                  >
                    Delete Event
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="event-chat-panel" style={chatPanel}>
            {ev.commentsLocked ? null : signedName ? (
              <div
                className="chat-name-row"
                style={{
                  background: '#fff',
                  border:
                    '1px solid #eee',
                  borderRadius: 5,
                  padding: '10px 12px',
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <strong>
                  Chat name:
                </strong>

                <span>
                  {signedName}
                </span>
              </div>
            ) : null}

            <div style={{ flex: 1 }}>
              <ChatBox
                eventId={ev.slug || id}
                name={chatName}
                isPublic={Boolean(ev.isPublic)}
                commentsLocked={Boolean(ev.commentsLocked)}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .grid-event-layout {
          display: grid;
          grid-template-columns: 1fr;
          row-gap: 1.5rem;
        }

        @media (max-width: 900px) {
          .grid-event-layout {
            grid-template-columns: 1fr;
            row-gap: 1.5rem;
          }
        }
      `}</style>
    </>
  );
}